import { useState, useEffect } from 'react'
import { XMarkIcon, PhotoIcon, TrashIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { api } from '../services/authService'
import Tesseract from 'tesseract.js'

// API Base URL for image paths
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

const VehicleEditForm = ({ vehicle, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('dados')
  const [vehicleTypes, setVehicleTypes] = useState([])
  const [loadingTypes, setLoadingTypes] = useState(true)
  const [formData, setFormData] = useState({
    type: 'excavator',
    vehicle_category: '',
    description: '',
    model: '',
    brand: '',
    ano_modelo: new Date().getFullYear(),
    ano_fabricacao: new Date().getFullYear(),
    license_plate: '',
    chassis: '',
    renavam: '',
    color: '',
    engine_power: '',
    engine_number: '',
    fuel_type: 'diesel',
    capacidade_carga: '',
    valor_franquia_seguro: '',
    valor_pneu_novo: '',
    percentual_depreciacao_pneu_por_mm: '',
    monthly_rate: '',
    status: 'available'
  })

  const [existingImages, setExistingImages] = useState([])
  const [newImages, setNewImages] = useState([])
  const [newPreviews, setNewPreviews] = useState([])
  const [documentImages, setDocumentImages] = useState([])
  const [documentPreviews, setDocumentPreviews] = useState([])
  const [ocrProcessing, setOcrProcessing] = useState(false)
  const [loading, setLoading] = useState(false)

  const [equipment, setEquipment] = useState([])
  const [selectedEquipment, setSelectedEquipment] = useState({}) // Mudado para objeto { equipmentId: quantity }
  const [equipmentSearch, setEquipmentSearch] = useState('')
  const [equipmentCategory, setEquipmentCategory] = useState('all')
  const [categories, setCategories] = useState([])
  const [kits, setKits] = useState([])
  const [selectedKit, setSelectedKit] = useState('')

  // Carregar tipos de veículos da API
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setLoadingTypes(true)
        const response = await api.get('/api/vehicle-types', {
          params: { active: 'true' }
        })

        if (response.data && response.data.data) {
          // Converter para formato do select
          const types = response.data.data.map(type => ({
            value: type.name,
            label: type.display_name,
            icon: type.icon
          }))
          setVehicleTypes(types)
        }
      } catch (error) {
        console.error('Erro ao carregar tipos de veículos:', error)
        // Fallback para tipos hardcoded em caso de erro
        setVehicleTypes([
          { value: 'truck', label: 'Caminhão', icon: '🚛' },
          { value: 'excavator', label: 'Escavadeira', icon: '🚜' },
          { value: 'crane', label: 'Guindaste', icon: '🏗️' }
        ])
      } finally {
        setLoadingTypes(false)
      }
    }

    fetchVehicleTypes()
  }, [])

  // Preencher formulário com dados existentes
  useEffect(() => {
    if (vehicle) {
      setFormData({
        type: vehicle.type || 'excavator',
        vehicle_category: vehicle.vehicle_category || '',
        description: vehicle.description || '',
        model: vehicle.model || '',
        brand: vehicle.brand || '',
        ano_modelo: vehicle.ano_modelo || vehicle.year || new Date().getFullYear(),
        ano_fabricacao: vehicle.ano_fabricacao || new Date().getFullYear(),
        license_plate: vehicle.license_plate || '',
        chassis: vehicle.chassis || '',
        renavam: vehicle.renavam || '',
        color: vehicle.color || '',
        engine_power: vehicle.engine_power || '',
        engine_number: vehicle.engine_number || '',
        fuel_type: vehicle.fuel_type || 'diesel',
        capacidade_carga: vehicle.capacidade_carga || '',
        valor_franquia_seguro: vehicle.valor_franquia_seguro || '',
        valor_pneu_novo: vehicle.valor_pneu_novo || '',
        percentual_depreciacao_pneu_por_mm: vehicle.percentual_depreciacao_pneu_por_mm || '',
        monthly_rate: vehicle.monthly_rate || '',
        status: vehicle.status || 'available'
      })

      setExistingImages(vehicle.images || [])
    }
  }, [vehicle])

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleNewImageChange = (e) => {
    const files = Array.from(e.target.files)
    const totalImages = existingImages.length + newImages.length

    if (files.length + totalImages > 5) {
      toast.error('Máximo 5 imagens por veículo')
      return
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    const invalidFiles = files.filter(file => !validTypes.includes(file.type))

    if (invalidFiles.length > 0) {
      toast.error('Apenas imagens JPG, PNG ou WEBP são permitidas')
      return
    }

    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024)
    if (oversizedFiles.length > 0) {
      toast.error('Imagens devem ter no máximo 5MB')
      return
    }

    const previews = files.map(file => ({
      file,
      url: URL.createObjectURL(file)
    }))

    setNewImages(prev => [...prev, ...files])
    setNewPreviews(prev => [...prev, ...previews])
  }

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index))
  }

  const removeNewImage = (index) => {
    URL.revokeObjectURL(newPreviews[index].url)
    setNewImages(prev => prev.filter((_, i) => i !== index))
    setNewPreviews(prev => prev.filter((_, i) => i !== index))
  }

  const handleDocumentChange = async (e) => {
    const files = Array.from(e.target.files)

    if (files.length === 0) return

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf']
    const invalidFiles = files.filter(file => !validTypes.includes(file.type))

    if (invalidFiles.length > 0) {
      toast.error('Apenas imagens JPG, PNG, WEBP ou PDF são permitidos')
      return
    }

    const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024)
    if (oversizedFiles.length > 0) {
      toast.error('Documentos devem ter no máximo 10MB')
      return
    }

    const previews = files.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }))

    setDocumentImages(prev => [...prev, ...files])
    setDocumentPreviews(prev => [...prev, ...previews])

    const imageFiles = files.filter(file => file.type.startsWith('image/'))
    if (imageFiles.length > 0) {
      processOCR(imageFiles[0])
    }
  }

  const processOCR = async (file) => {
    setOcrProcessing(true)
    toast.loading('Processando documento com OCR...', { id: 'ocr' })

    try {
      const { data: { text } } = await Tesseract.recognize(file, 'por', {
        logger: m => console.log(m)
      })

      console.log('Texto extraído:', text)

      const extractedData = extractVehicleData(text)

      if (Object.keys(extractedData).length > 0) {
        setFormData(prev => ({ ...prev, ...extractedData }))
        toast.success(`Dados extraídos: ${Object.keys(extractedData).join(', ')}`, { id: 'ocr' })
      } else {
        toast('Documento processado, mas nenhum dado relevante foi encontrado', {
          id: 'ocr',
          icon: 'ℹ️'
        })
      }
    } catch (error) {
      console.error('Erro no OCR:', error)
      toast.error('Erro ao processar documento', { id: 'ocr' })
    } finally {
      setOcrProcessing(false)
    }
  }

  const extractVehicleData = (text) => {
    const data = {}

    const placaMatch = text.match(/([A-Z]{3}[-\s]?\d{4})/i)
    if (placaMatch) {
      data.license_plate = placaMatch[1].replace(/\s/g, '-').toUpperCase()
    }

    const renavamMatch = text.match(/RENAVAM[:\s]*(\d{11})/i)
    if (renavamMatch) {
      data.renavam = renavamMatch[1]
    }

    const chassisMatch = text.match(/CHASSIS[:\s]*([A-Z0-9]{17})/i) ||
                         text.match(/CHASSI[:\s]*([A-Z0-9]{17})/i)
    if (chassisMatch) {
      data.chassis = chassisMatch[1].toUpperCase()
    }

    const anoMatch = text.match(/(\d{4})\/(\d{4})/)
    if (anoMatch) {
      data.ano_fabricacao = parseInt(anoMatch[1])
      data.ano_modelo = parseInt(anoMatch[2])
    }

    const marcaModeloMatch = text.match(/MARCA[:\s]*([A-Z\s]+).*MODELO[:\s]*([A-Z0-9\s-]+)/i)
    if (marcaModeloMatch) {
      data.brand = marcaModeloMatch[1].trim()
      data.model = marcaModeloMatch[2].trim()
    }

    const corMatch = text.match(/COR[:\s]*([A-Z\s]+)/i)
    if (corMatch) {
      data.color = corMatch[1].trim()
    }

    return data
  }

  const removeDocumentImage = (index) => {
    URL.revokeObjectURL(documentPreviews[index].url)
    setDocumentImages(prev => prev.filter((_, i) => i !== index))
    setDocumentPreviews(prev => prev.filter((_, i) => i !== index))
  }

  // Load equipment and categories when equipment tab is opened
  useEffect(() => {
    if (activeTab === 'equipamentos') {
      loadEquipmentData()
    }
  }, [activeTab])

  const loadEquipmentData = async () => {
    try {
      // Load categories
      const categoriesResponse = await api.get('/api/auxiliary-equipment/categories')
      console.log('Categories API response:', categoriesResponse.data)

      // API retorna { data: { flat: [...], tree: [...] } }
      const apiData = categoriesResponse.data.data || categoriesResponse.data

      // Se apiData tem propriedade 'flat', usar ela; senão assumir que já é array
      const categoriesArray = apiData?.flat || apiData || []

      setCategories(Array.isArray(categoriesArray) ? categoriesArray : [])

      console.log(`✅ ${Array.isArray(categoriesArray) ? categoriesArray.length : 0} categorias carregadas para dropdown`)

      // Load all equipment
      const equipmentResponse = await api.get('/api/auxiliary-equipment?status=available')
      setEquipment(equipmentResponse.data.data || [])

      // Load kits
      try {
        const kitsResponse = await api.get('/api/auxiliary-equipment/kits')
        setKits(kitsResponse.data.data || [])
        console.log(`✅ ${(kitsResponse.data.data || []).length} kits carregados`)
      } catch (kitError) {
        console.warn('Aviso: Não foi possível carregar kits:', kitError)
        setKits([])
      }

      // Load existing assignments for this vehicle
      if (vehicle.id) {
        const assignmentsResponse = await api.get(`/api/equipment-assignments?vehicle_id=${vehicle.id}&status=assigned`)
        const existingAssignments = assignmentsResponse.data.data || []

        // Converter para objeto { equipmentId: quantity }
        const equipmentQuantities = {}
        existingAssignments.forEach(assignment => {
          const equipmentId = assignment.equipment_id
          equipmentQuantities[equipmentId] = (equipmentQuantities[equipmentId] || 0) + (assignment.quantity || 1)
        })

        setSelectedEquipment(equipmentQuantities)
      }
    } catch (error) {
      console.error('Erro ao carregar equipamentos:', error)
      toast.error('Erro ao carregar equipamentos')
    }
  }

  // Aplicar kit selecionado - marca todos os equipamentos do kit
  const applyKit = async (kitId) => {
    if (!kitId) {
      setSelectedKit('')
      return
    }

    try {
      // Carregar detalhes do kit com seus itens
      const response = await api.get(`/api/auxiliary-equipment/kits/${kitId}`)
      const kit = response.data.data

      if (kit && kit.items && kit.items.length > 0) {
        // Criar novo objeto de equipamentos selecionados
        const newSelectedEquipment = { ...selectedEquipment }

        // Adicionar todos os equipamentos do kit
        kit.items.forEach(item => {
          newSelectedEquipment[item.equipment_id] = item.quantity || 1
        })

        setSelectedEquipment(newSelectedEquipment)
        setSelectedKit(kitId)
        toast.success(`Kit "${kit.name}" aplicado! ${kit.items.length} equipamento(s) adicionado(s).`)
      } else {
        toast.error('Kit vazio ou não encontrado')
      }
    } catch (error) {
      console.error('Erro ao aplicar kit:', error)
      toast.error('Erro ao carregar detalhes do kit')
    }
  }

  const saveEquipmentAssignments = async () => {
    try {
      // Get existing assignments
      const assignmentsResponse = await api.get(`/api/equipment-assignments?vehicle_id=${vehicle.id}&status=assigned`)
      const existingAssignments = assignmentsResponse.data.data || []

      // Agrupar atribuições existentes por equipment_id
      const existingByEquipment = {}
      existingAssignments.forEach(assignment => {
        const equipId = assignment.equipment_id
        if (!existingByEquipment[equipId]) {
          existingByEquipment[equipId] = []
        }
        existingByEquipment[equipId].push(assignment)
      })

      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const assigned_by = user.id || '32a11ffb-db6d-44e4-87c0-bf079f14e018'

      let addedCount = 0
      let removedCount = 0

      // Processar cada equipamento selecionado
      for (const [equipmentId, desiredQuantity] of Object.entries(selectedEquipment)) {
        const existingAssigns = existingByEquipment[equipmentId] || []
        const currentQuantity = existingAssigns.length

        if (desiredQuantity > currentQuantity) {
          // Adicionar mais atribuições
          const toAddCount = desiredQuantity - currentQuantity
          const equipmentIdsToAdd = Array(toAddCount).fill(equipmentId)

          await api.post('/api/equipment-assignments', {
            equipment_ids: equipmentIdsToAdd,
            vehicle_id: vehicle.id,
            assigned_by: assigned_by,
            condition_on_assignment: 'good',
            notes_on_assignment: 'Atribuição automática via edição de veículo'
          })

          addedCount += toAddCount
        } else if (desiredQuantity < currentQuantity) {
          // Remover atribuições extras
          const toRemoveCount = currentQuantity - desiredQuantity
          const assignmentsToRemove = existingAssigns.slice(0, toRemoveCount)

          for (const assignment of assignmentsToRemove) {
            // Usar a maior data entre hoje e a data de atribuição
            const assignmentDate = new Date(assignment.assignment_date)
            const today = new Date()
            const returnDate = assignmentDate > today ? assignmentDate : today

            await api.put(`/api/equipment-assignments/${assignment.id}`, {
              status: 'returned',
              end_date: returnDate.toISOString().split('T')[0]
            })
          }

          removedCount += toRemoveCount
        }

        // Remover do objeto para rastrear quais foram processados
        delete existingByEquipment[equipmentId]
      }

      // Remover equipamentos que não estão mais selecionados
      for (const [equipmentId, assignments] of Object.entries(existingByEquipment)) {
        for (const assignment of assignments) {
          // Usar a maior data entre hoje e a data de atribuição
          const assignmentDate = new Date(assignment.assignment_date)
          const today = new Date()
          const returnDate = assignmentDate > today ? assignmentDate : today

          await api.put(`/api/equipment-assignments/${assignment.id}`, {
            status: 'returned',
            end_date: returnDate.toISOString().split('T')[0]
          })
          removedCount++
        }
      }

      if (addedCount > 0 || removedCount > 0) {
        console.log(`✅ Equipamentos atualizados: ${addedCount} adicionados, ${removedCount} removidos`)
      }
    } catch (error) {
      console.error('Erro ao salvar equipamentos:', error)
      toast.error('Erro ao salvar atribuições de equipamentos')
      throw error
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      available: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', label: 'Disponível' },
      rented: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Em Uso' },
      maintenance: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', label: 'Manutenção' },
      inactive: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: 'Inativo' }
    }
    const badge = badges[status] || badges.available
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      console.log('📝 Atualizando veículo ID:', vehicle.id)

      const submitData = new FormData()

      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key])
      })

      submitData.append('existingImages', JSON.stringify(existingImages))

      newImages.forEach(image => {
        submitData.append('images', image)
      })

      const response = await api.put(`/api/vehicles/${vehicle.id}`, submitData)
      const result = response.data
      console.log('📋 Resposta do servidor:', result)

      // Save equipment assignments
      await saveEquipmentAssignments()

      toast.success('Veículo atualizado com sucesso!')
      onSuccess && onSuccess()
      onClose()
    } catch (error) {
      console.error('❌ Erro na requisição:', error)
      const message = error.response?.data?.error || 'Erro ao atualizar veículo'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  if (!vehicle) return null

  const tabs = [
    { id: 'dados', label: '📋 Dados do Veículo' },
    { id: 'valores', label: '💰 Valores' },
    { id: 'equipamentos', label: '🔧 Equipamentos' },
    { id: 'fotos', label: '📷 Fotos' },
    { id: 'documentos', label: '📄 Multas e Documentos' }
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header com Status */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              ✏️ Editar Veículo - {vehicle.model}
            </h2>
            {getStatusBadge(formData.status)}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">

            {/* ABA 1: DADOS DO VEÍCULO */}
            {activeTab === 'dados' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Veículo *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    disabled={loadingTypes}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {loadingTypes ? 'Carregando tipos...' : 'Selecione um tipo'}
                    </option>
                    {vehicleTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.icon ? `${type.icon} ` : ''}{type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Categoria de Vistoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoria de Vistoria *
                    <span className="ml-1 text-xs text-gray-500 dark:text-gray-400 font-normal">(define o checklist correto)</span>
                  </label>
                  <select
                    name="vehicle_category"
                    value={formData.vehicle_category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Selecione uma categoria</option>
                    <option value="pintura_frio">🎨 Pintura a Frio</option>
                    <option value="munck">🏗️ Munck / Guindaste</option>
                    <option value="bate_estaca">⚙️ Bate-Estaca</option>
                    <option value="carroceria">🚛 Carroceria / Flatbed</option>
                  </select>
                </div>

                {/* Descrição */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descrição
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Ex: Caminhão basculante com caçamba reforçada"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Modelo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Modelo *
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: CAT-320"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Marca */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Marca *
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Caterpillar"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Ano Modelo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ano Modelo *
                  </label>
                  <input
                    type="number"
                    name="ano_modelo"
                    value={formData.ano_modelo}
                    onChange={handleInputChange}
                    required
                    min="1980"
                    max={new Date().getFullYear() + 1}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Ano Fabricação */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ano Fabricação *
                  </label>
                  <input
                    type="number"
                    name="ano_fabricacao"
                    value={formData.ano_fabricacao}
                    onChange={handleInputChange}
                    required
                    min="1980"
                    max={new Date().getFullYear() + 1}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Placa */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Placa *
                  </label>
                  <input
                    type="text"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: ABC-1234"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Chassis */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Chassis
                  </label>
                  <input
                    type="text"
                    name="chassis"
                    value={formData.chassis}
                    onChange={handleInputChange}
                    placeholder="Ex: 9BD123456789"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* RENAVAM */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    RENAVAM
                  </label>
                  <input
                    type="text"
                    name="renavam"
                    value={formData.renavam}
                    onChange={handleInputChange}
                    placeholder="Ex: 00123456789"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Cor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cor
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    placeholder="Ex: Amarelo"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Potência do Motor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Potência do Motor
                  </label>
                  <input
                    type="text"
                    name="engine_power"
                    value={formData.engine_power}
                    onChange={handleInputChange}
                    placeholder="Ex: 174 HP"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Número do Motor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Número do Motor
                  </label>
                  <input
                    type="text"
                    name="engine_number"
                    value={formData.engine_number}
                    onChange={handleInputChange}
                    placeholder="Ex: 1234ABC5678"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Tipo de Combustível */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Combustível
                  </label>
                  <select
                    name="fuel_type"
                    value={formData.fuel_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="diesel">Diesel</option>
                    <option value="gasoline">Gasolina</option>
                    <option value="electric">Elétrico</option>
                    <option value="hybrid">Híbrido</option>
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="available">Disponível</option>
                    <option value="rented">Alugado</option>
                    <option value="maintenance">Manutenção</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
              </div>
            )}

            {/* ABA 2: VALORES */}
            {activeTab === 'valores' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Valor Mensal */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    💵 Valor Aluguel Mensal (R$) *
                  </label>
                  <input
                    type="number"
                    name="monthly_rate"
                    value={formData.monthly_rate}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="Ex: 350.00"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Capacidade de Carga */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    🏋️ Capacidade de Carga (kg)
                  </label>
                  <input
                    type="number"
                    name="capacidade_carga"
                    value={formData.capacidade_carga}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="Ex: 25000"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Valor Franquia Seguro */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    🛡️ Valor Franquia Seguro (R$)
                  </label>
                  <input
                    type="number"
                    name="valor_franquia_seguro"
                    value={formData.valor_franquia_seguro}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="Ex: 5000.00"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Valor Pneu Novo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    🛞 Valor Pneu Novo (R$)
                  </label>
                  <input
                    type="number"
                    name="valor_pneu_novo"
                    value={formData.valor_pneu_novo}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="Ex: 2500.00"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Percentual Depreciação Pneu */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    📉 Deprec. Pneu por mm (%)
                  </label>
                  <input
                    type="number"
                    name="percentual_depreciacao_pneu_por_mm"
                    value={formData.percentual_depreciacao_pneu_por_mm}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="Ex: 5.00"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Informação sobre valores */}
                <div className="md:col-span-2">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                      💡 Informações sobre Valores
                    </h4>
                    <ul className="text-xs text-blue-800 dark:text-blue-400 space-y-1">
                      <li>• <strong>Valor Mensal:</strong> Valor cobrado do cliente por mês de locação</li>
                      <li>• <strong>Franquia Seguro:</strong> Valor que o cliente paga em caso de sinistro</li>
                      <li>• <strong>Valor Pneu Novo:</strong> Usado para calcular cobranças por desgaste</li>
                      <li>• <strong>Depreciação por mm:</strong> Percentual cobrado por mm de desgaste do pneu</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* ABA 3: EQUIPAMENTOS */}
            {activeTab === 'equipamentos' && (
              <div className="space-y-4">
                {/* Seletor de Kit */}
                {kits.length > 0 && (
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          📦 Aplicar Kit de Equipamentos
                        </label>
                        <select
                          value={selectedKit}
                          onChange={(e) => applyKit(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">Selecione um kit para aplicar...</option>
                          {kits.map(kit => (
                            <option key={kit.id} value={kit.id}>
                              {kit.name} {kit.vehicle_type ? `(${kit.vehicle_type})` : ''} - {kit.item_count || 0} itens
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>Selecione um kit para adicionar automaticamente</p>
                        <p>todos os equipamentos que ele contém.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Search and Filter */}
                <div className="flex gap-3">
                  <input
                    type="text"
                    id="equipment-search"
                    placeholder="🔍 Buscar equipamento..."
                    value={equipmentSearch}
                    onChange={(e) => setEquipmentSearch(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <select
                    id="equipment-category-filter"
                    value={equipmentCategory}
                    onChange={(e) => setEquipmentCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="all">Todas Categorias</option>
                    {(categories || []).map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Equipment Grid */}
                <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(equipment || [])
                      .filter(item => {
                        const matchesSearch = item.name.toLowerCase().includes(equipmentSearch.toLowerCase())
                        const matchesCategory = equipmentCategory === 'all' || item.category_id === equipmentCategory
                        return matchesSearch && matchesCategory
                      })
                      .map(item => {
                        const isSelected = selectedEquipment[item.id] > 0
                        const currentQuantity = selectedEquipment[item.id] || 0
                        const availableStock = item.quantity_available || 0

                        return (
                          <div
                            key={item.id}
                            className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                const newSelected = { ...selectedEquipment }
                                if (e.target.checked) {
                                  newSelected[item.id] = 1 // Iniciar com quantidade 1
                                } else {
                                  delete newSelected[item.id] // Remover do objeto
                                }
                                setSelectedEquipment(newSelected)
                              }}
                              className="mt-1 w-4 h-4 cursor-pointer"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {item.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {(categories || []).find(c => c.id === item.category_id)?.name || 'Sem categoria'}
                              </p>
                              <p className="text-xs font-semibold text-green-600 dark:text-green-400 mt-1">
                                +R$ {Number(item.monthly_rate || 0).toFixed(2)}/mês
                              </p>
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                📦 Disponível: {availableStock} unidades
                              </p>
                            </div>
                            {isSelected && (
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-600 dark:text-gray-400">Qtd:</label>
                                <input
                                  type="number"
                                  min="1"
                                  max={availableStock}
                                  value={currentQuantity}
                                  onChange={(e) => {
                                    const newQuantity = parseInt(e.target.value) || 1
                                    const validQuantity = Math.min(Math.max(1, newQuantity), availableStock)
                                    setSelectedEquipment({
                                      ...selectedEquipment,
                                      [item.id]: validQuantity
                                    })
                                  }}
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            )}
                          </div>
                        )
                      })}
                  </div>
                  {(equipment || []).filter(item => {
                    const matchesSearch = item.name.toLowerCase().includes(equipmentSearch.toLowerCase())
                    const matchesCategory = equipmentCategory === 'all' || item.category_id === equipmentCategory
                    return matchesSearch && matchesCategory
                  }).length === 0 && (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                      Nenhum equipamento encontrado
                    </p>
                  )}
                </div>

                {/* Summary Panel */}
                <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        📦 Equipamentos Selecionados
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {Object.keys(selectedEquipment).length} tipo(s)
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {Object.values(selectedEquipment).reduce((sum, qty) => sum + qty, 0)} unidades no total
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        💰 Valor Adicional/Mês
                      </p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        R$ {(equipment || [])
                          .filter(item => selectedEquipment[item.id] > 0)
                          .reduce((sum, item) => {
                            const quantity = selectedEquipment[item.id] || 0
                            return sum + (Number(item.monthly_rate || 0) * quantity)
                          }, 0)
                          .toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ABA 4: FOTOS */}
            {activeTab === 'fotos' && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <PhotoIcon className="w-5 h-5 mr-2" />
                    Galeria de Fotos do Veículo
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Adicione até 5 fotos do veículo. Formatos aceitos: JPG, PNG, WEBP (máximo 5MB cada)
                  </p>

                  {/* Imagens Existentes */}
                  {existingImages.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        📸 Fotos Atuais ({existingImages.length}/5)
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {existingImages.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={`${API_BASE_URL}${image.url}`}
                              alt={`Imagem ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer"
                              onClick={() => window.open(`${API_BASE_URL}${image.url}`, '_blank')}
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingImage(index)}
                              className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg opacity-0 group-hover:opacity-100"
                              title="Remover foto"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload de Novas Imagens */}
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
                    <input
                      type="file"
                      id="new-images"
                      multiple
                      accept="image/*"
                      onChange={handleNewImageChange}
                      className="hidden"
                      disabled={existingImages.length + newImages.length >= 5}
                    />
                    <label
                      htmlFor="new-images"
                      className={`cursor-pointer flex flex-col items-center justify-center ${
                        existingImages.length + newImages.length >= 5
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      <PhotoIcon className="w-16 h-16 text-gray-400 mb-3" />
                      <p className="text-base font-medium text-gray-700 dark:text-gray-300 text-center mb-1">
                        {existingImages.length + newImages.length >= 5
                          ? '✓ Limite máximo de 5 imagens atingido'
                          : '📤 Clique para adicionar mais fotos'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        JPG, PNG ou WEBP - máximo 5MB cada
                      </p>
                    </label>
                  </div>

                  {/* Preview de Novas Imagens */}
                  {newPreviews.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        🆕 Novas fotos a serem adicionadas ({newPreviews.length})
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {newPreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview.url}
                              alt={`Nova imagem ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border-2 border-green-200 dark:border-green-600"
                            />
                            <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded">
                              NOVA
                            </div>
                            <button
                              type="button"
                              onClick={() => removeNewImage(index)}
                              className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ABA 5: MULTAS E DOCUMENTOS */}
            {activeTab === 'documentos' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                    <DocumentArrowUpIcon className="w-6 h-6 mr-2 text-blue-600" />
                    Upload de Documentos com OCR
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    📄 Faça upload de documentos do veículo (CRLV, nota fiscal, etc.) e o sistema irá <strong>extrair automaticamente</strong> os dados usando OCR (Reconhecimento Ótico de Caracteres).
                  </p>

                  {/* Upload Area */}
                  <div className="border-2 border-dashed border-blue-300 dark:border-blue-600 rounded-lg p-8 bg-white dark:bg-gray-800 hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      id="document-upload"
                      multiple
                      accept="image/*,application/pdf"
                      onChange={handleDocumentChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="document-upload"
                      className="cursor-pointer flex flex-col items-center justify-center"
                    >
                      <DocumentArrowUpIcon className="w-20 h-20 text-blue-500 mb-3" />
                      <p className="text-lg font-medium text-gray-900 dark:text-white text-center mb-2">
                        📤 Clique para fazer upload de documentos
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                        Formatos aceitos: JPG, PNG, WEBP, PDF (máximo 10MB)
                      </p>
                      {ocrProcessing && (
                        <div className="mt-4 flex items-center space-x-2 text-blue-600 dark:text-blue-400">
                          <div className="spinner w-5 h-5"></div>
                          <span className="text-sm font-medium">Processando documento com OCR...</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Informações sobre OCR */}
                  <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                      🤖 O que o OCR pode extrair automaticamente:
                    </h4>
                    <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-1 grid grid-cols-2 gap-2">
                      <li>✓ Placa do veículo</li>
                      <li>✓ RENAVAM (11 dígitos)</li>
                      <li>✓ Número do Chassis</li>
                      <li>✓ Ano de Fabricação/Modelo</li>
                      <li>✓ Marca do veículo</li>
                      <li>✓ Modelo do veículo</li>
                      <li>✓ Cor do veículo</li>
                      <li>✓ E outros dados presentes</li>
                    </ul>
                  </div>

                  {/* Preview de Documentos */}
                  {documentPreviews.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        📋 Documentos Enviados ({documentPreviews.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {documentPreviews.map((preview, index) => (
                          <div key={index} className="relative group bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:shadow-lg transition-shadow">
                            {preview.file.type.startsWith('image/') ? (
                              <img
                                src={preview.url}
                                alt={preview.name}
                                className="w-full h-40 object-cover rounded"
                              />
                            ) : (
                              <div className="w-full h-40 flex items-center justify-center bg-gray-100 dark:bg-gray-600 rounded">
                                <div className="text-center">
                                  <DocumentArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                                  <p className="text-xs text-gray-600 dark:text-gray-300">PDF</p>
                                </div>
                              </div>
                            )}
                            <p className="text-xs text-gray-600 dark:text-gray-300 mt-2 truncate">
                              {preview.name}
                            </p>
                            <button
                              type="button"
                              onClick={() => removeDocumentImage(index)}
                              className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg opacity-0 group-hover:opacity-100"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Seção de Multas (Placeholder - pode ser expandida) */}
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 border border-yellow-200 dark:border-yellow-800">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                    🚨 Gestão de Multas
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Funcionalidade em desenvolvimento. Em breve você poderá registrar e gerenciar multas associadas a este veículo.
                  </p>
                </div>
              </div>
            )}

          </div>

          {/* Footer com botões */}
          <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Aba ativa: <strong>{tabs.find(t => t.id === activeTab)?.label}</strong>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? '📤 Salvando...' : '💾 Salvar Alterações'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default VehicleEditForm
